import type { LocalTrackPublication, RemoteTrack, Room, Track } from "livekit-client";
import type { Session } from "./session";
import { ring } from "./sound";
import type { UUID } from "./sync/types";

// A huddle is a channel's call (ADR-0023), on LiveKit, which the server
// reaches for the client at /livekit. The call is joined with the
// microphone on and the camera off. livekit-client is loaded only when one
// first joins a call. A DM's call is a phone call: it rings until someone
// answers, and is over once everyone else has hung up.

// Tile is a video on the stage: someone's camera or screen.
export interface Tile {
  key: string;
  who: UUID;
  screen: boolean;
  track: Track;
}

// Member is someone in the call, as the room has them: whether it is
// oneself, and whether their microphone is off.
export interface Member {
  id: UUID;
  me: boolean;
  micOff: boolean;
}

export interface CallState {
  channel: UUID;
  connecting: boolean;
  // calling is a DM's call nobody else has answered yet.
  calling: boolean;
  muted: boolean;
  camera: boolean;
  screen: boolean;
  speaking: UUID[];
  tiles: Tile[];
  // members are those in the room, in the order they joined, oneself last.
  members: Member[];
  error: string;
  // since is when one got in, for the call's clock.
  since: number | null;
  // devices are the microphone, camera and speaker chosen, by device ID;
  // none chosen is the system's.
  devices: Devices;
}

export type DeviceKind = "audioinput" | "videoinput" | "audiooutput";
export type Devices = Partial<Record<DeviceKind, string>>;

const devicesKey = "lantern.call-devices";

// savedDevices are the devices chosen in this browser, for every call.
function savedDevices(): Devices {
  try {
    const v = JSON.parse(localStorage.getItem(devicesKey) ?? "{}");
    return v && typeof v === "object" ? v : {};
  } catch {
    return {};
  }
}

function saveDevices(d: Devices) {
  try {
    localStorage.setItem(devicesKey, JSON.stringify(d));
  } catch {
    // Private windows may refuse; the choice lasts the call.
  }
}

type Listener = () => void;

// Call is the one call this tab is in, if any, as React reads it.
class Call {
  private room: Room | null = null;
  private state: CallState | null = null;
  private listeners = new Set<Listener>();
  private audio = new Map<string, HTMLMediaElement>();
  private session: Session | null = null;
  // dm is the kind of DM the call is in: with one other person, or more.
  private dm: "one" | "group" | null = null;
  private stopCalling: (() => void) | null = null;
  // gen counts joins and leaves: a join's later steps stop once another
  // has begun, or the call was left.
  private gen = 0;
  private livekit: Promise<typeof import("livekit-client")> | null = null;

  subscribe = (l: Listener) => {
    this.listeners.add(l);
    return () => void this.listeners.delete(l);
  };
  snapshot = () => this.state;

  private emit(state: CallState | null) {
    this.state = state;
    this.listeners.forEach((l) => l());
  }

  // set changes the call's state. A change that comes once the call is
  // over, as a microphone failing to start in a call just left, is dropped.
  private set(patch: Partial<CallState>) {
    if (this.state) this.emit({ ...this.state, ...patch });
  }

  // join leaves any other call and joins a channel's.
  async join(session: Session, channel: UUID) {
    if (this.state?.channel === channel) return;
    await this.leave();
    const mine = ++this.gen;
    const stale = () => this.gen !== mine;
    const kind = session.store.getSnapshot().channels.get(channel)?.channel.kind;
    this.session = session;
    this.dm = kind === "dm" ? "one" : kind === "group_dm" ? "group" : null;
    const devices = savedDevices();
    this.emit({ channel, connecting: true, calling: false, muted: false, camera: false, screen: false, speaking: [], tiles: [], members: [], error: "", since: null, devices });
    try {
      const [{ Room, RoomEvent }, join] = await Promise.all([(this.livekit ??= import("livekit-client")), session.api.joinHuddle(channel)]);
      if (stale()) return;
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        audioCaptureDefaults: devices.audioinput ? { deviceId: devices.audioinput } : undefined,
        videoCaptureDefaults: devices.videoinput ? { deviceId: devices.videoinput } : undefined,
        audioOutput: devices.audiooutput ? { deviceId: devices.audiooutput } : undefined,
      });
      this.room = room;
      const tiles = () => this.set({ tiles: tilesOf(room), members: membersOf(room) });
      room
        .on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
          if (track.kind === "audio") {
            const el = track.attach();
            el.style.display = "none";
            document.body.append(el);
            this.audio.set(track.sid ?? "", el);
          }
          tiles();
        })
        .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
          track.detach().forEach((el) => el.remove());
          this.audio.delete(track.sid ?? "");
          tiles();
        })
        .on(RoomEvent.TrackMuted, tiles)
        .on(RoomEvent.TrackUnmuted, tiles)
        .on(RoomEvent.ParticipantConnected, () => {
          if (this.room === room) this.answered();
          tiles();
        })
        .on(RoomEvent.ParticipantDisconnected, () => {
          tiles();
          if (this.room !== room || !this.dm || this.stopCalling) return;
          // After a moment's grace, as someone moving to another device
          // leaves and comes back.
          setTimeout(() => {
            if (this.room === room && !this.stopCalling && room.remoteParticipants.size === 0) void this.leave();
          }, 3000);
        })
        .on(RoomEvent.LocalTrackPublished, tiles)
        .on(RoomEvent.LocalTrackUnpublished, (pub: LocalTrackPublication) => {
          if (pub.source === "screen_share") this.set({ screen: false });
          tiles();
        })
        .on(RoomEvent.ActiveSpeakersChanged, (ps) => this.set({ speaking: ps.map((p) => p.identity) }))
        .on(RoomEvent.Disconnected, () => {
          if (this.room === room) void this.leave();
        });
      const scheme = location.protocol === "https:" ? "wss:" : "ws:";
      await room.connect(`${scheme}//${location.host}${join.path}`, join.token);
      if (stale()) return void room.disconnect();
      this.set({ connecting: false, members: membersOf(room), since: Date.now() });
      // A device chosen while connecting is used now.
      const chosen = savedDevices();
      for (const kind of ["audioinput", "videoinput", "audiooutput"] as DeviceKind[]) {
        if (chosen[kind] && chosen[kind] !== devices[kind]) await room.switchActiveDevice(kind, chosen[kind]).catch(() => {});
      }
      if (this.dm && room.remoteParticipants.size === 0) this.ringOut();
      // Muted while connecting, one stays muted.
      if (!this.state?.muted) await room.localParticipant.setMicrophoneEnabled(true).catch(() => this.set({ muted: true, error: "microphone" }));
      if (stale()) stopLocal(room);
    } catch (err) {
      if (!stale()) {
        await this.leave();
        throw err;
      }
    }
  }

  // ringOut rings while nobody else is in a DM's call; with nobody after 35
  // seconds, the call is given up.
  private ringOut() {
    const stop = ring("outgoing");
    const timer = setTimeout(() => void this.end("no_answer"), 35_000);
    this.stopCalling = () => {
      stop();
      clearTimeout(timer);
    };
    this.set({ calling: true });
  }

  private answered() {
    if (!this.stopCalling) return;
    this.stopCalling();
    this.stopCalling = null;
    this.set({ calling: false });
  }

  // declined hears someone declined a channel's call: calling the one
  // other person in a DM, the call is over.
  declined(channel: UUID, who: UUID) {
    if (this.state?.channel === channel && this.stopCalling && this.dm === "one") void this.end("declined", who);
  }

  private async end(why: "declined" | "no_answer", who?: UUID) {
    const session = this.session;
    const channel = this.state?.channel;
    await this.leave();
    if (session && channel) session.callEnded(channel, why, who);
  }

  // leaveFor leaves the call if a session made it, as that session ends.
  async leaveFor(session: Session) {
    if (this.session === session) await this.leave();
  }

  async leave() {
    const room = this.room;
    this.room = null;
    this.session = null;
    this.gen++;
    this.stopCalling?.();
    this.stopCalling = null;
    this.audio.forEach((el) => el.remove());
    this.audio.clear();
    this.emit(null);
    await room?.disconnect();
  }

  // setMuted mutes or unmutes; while connecting, the choice is kept for
  // when the microphone starts.
  async setMuted(muted: boolean) {
    const room = this.room;
    if (room && !this.state?.connecting) await room.localParticipant.setMicrophoneEnabled(!muted);
    if (room && room !== this.room) return stopLocal(room);
    this.set({ muted, error: "", members: room ? membersOf(room) : [] });
  }

  async setCamera(on: boolean) {
    const room = this.room;
    await room?.localParticipant.setCameraEnabled(on);
    if (room && room !== this.room) return stopLocal(room);
    this.set({ camera: on, tiles: room ? tilesOf(room) : [], members: room ? membersOf(room) : [] });
  }

  // devicesOf lists this computer's microphones, cameras or speakers. Their
  // names show once the browser has let the page use them.
  // Until the browser has let the page use a kind of device it names none,
  // so the list asks it to.
  async devicesOf(kind: DeviceKind): Promise<MediaDeviceInfo[]> {
    const { Room } = await (this.livekit ??= import("livekit-client"));
    const named = (list: MediaDeviceInfo[]) => list.filter((d) => d.deviceId);
    const list = named(await Room.getLocalDevices(kind, false).catch(() => []));
    if (list.length > 0 || kind === "audiooutput") return list;
    return named(await Room.getLocalDevices(kind, true).catch(() => []));
  }

  // setDevice uses a device for the call, and for calls after it; one that
  // cannot be used is said so, and not kept.
  async setDevice(kind: DeviceKind, id: string) {
    const room = this.room;
    if (room && !this.state?.connecting) {
      const ok = await room.switchActiveDevice(kind, id).then(
        (done) => done !== false,
        () => false,
      );
      if (!ok) return this.set({ error: "device" });
    }
    const devices = { ...savedDevices(), [kind]: id };
    saveDevices(devices);
    this.set({ devices, error: "" });
  }

  async setScreen(on: boolean) {
    const room = this.room;
    // Refused when the person cancels the browser's picker.
    await room?.localParticipant.setScreenShareEnabled(on).then(
      () => this.set({ screen: on }),
      () => this.set({ screen: false }),
    );
    if (room && room !== this.room) return stopLocal(room);
    if (room) this.set({ tiles: tilesOf(room), members: membersOf(room) });
  }
}

// stopLocal stops one's own tracks in a room already left, as a camera
// or microphone that finished starting after one left: else its light
// stays on.
function stopLocal(room: Room) {
  for (const pub of room.localParticipant.trackPublications.values()) pub.track?.stop();
}

// membersOf is who is in the room, in the order they joined, oneself last.
function membersOf(room: Room): Member[] {
  return [...room.remoteParticipants.values(), room.localParticipant].map((p) => ({
    id: p.identity,
    me: p === room.localParticipant,
    micOff: !p.isMicrophoneEnabled,
  }));
}

// tilesOf is the room's videos: screens first, then cameras, one's own last.
function tilesOf(room: Room): Tile[] {
  const out: Tile[] = [];
  for (const p of [...room.remoteParticipants.values(), room.localParticipant]) {
    for (const pub of p.trackPublications.values()) {
      if (pub.kind !== "video" || !pub.track || pub.isMuted) continue;
      out.push({ key: `${p.identity}:${pub.trackSid}`, who: p.identity, screen: pub.source === "screen_share", track: pub.track });
    }
  }
  return out.sort((a, b) => Number(b.screen) - Number(a.screen));
}

export const call = new Call();
